import {Autocomplete, TextInputWithTokens} from '@primer/react'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'

import styles from './AutocompleteTokenTagInput.module.css'

export type AutocompleteItem = {
  id: string
  text: string
  color?: string
  description?: string
  leadingVisual?: React.FC
}

type TokenItem = {
  id: string
  text: string
}

type SelectedItem = {
  id: string
  text?: string
}

export type FetchSuggestions = (query: string, signal: AbortSignal) => Promise<AutocompleteItem[]>

type AutocompleteTokenTagInputProps = {
  values: string[]
  onChange: (values: string[]) => void
  variant?: 'allowed' | 'blocked'
  'aria-label': string
  fetchSuggestions?: FetchSuggestions
  placeholder?: string
  emptyText?: string
}

export function AutocompleteTokenTagInput({
  values,
  onChange,
  variant = 'allowed',
  fetchSuggestions,
  placeholder = 'Search...',
  emptyText = 'No results found',
  ...rest
}: AutocompleteTokenTagInputProps) {
  const [filterVal, setFilterVal] = useState('')
  const [items, setItems] = useState<AutocompleteItem[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const abortRef = useRef<AbortController | null>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Fetch suggestions when filter text changes
  useEffect(() => {
    if (!fetchSuggestions || !filterVal.trim()) {
      return
    }

    if (debounceRef.current) clearTimeout(debounceRef.current)
    if (abortRef.current) abortRef.current.abort()

    debounceRef.current = setTimeout(() => {
      const controller = new AbortController()
      abortRef.current = controller

      void (async () => {
        try {
          const results = await fetchSuggestions(filterVal.trim(), controller.signal)
          if (controller.signal.aborted) return
          setItems(results)
          setIsLoading(false)
        } catch (err) {
          if (err instanceof DOMException && err.name === 'AbortError') return
          setItems([])
          setIsLoading(false)
        }
      })()
    }, 200)

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [filterVal, fetchSuggestions])

  const tokens: TokenItem[] = useMemo(() => values.map(v => ({id: v, text: v})), [values])

  const selectedItemIds = useMemo(() => values.map(v => v), [values])

  const onTokenRemove = useCallback(
    (tokenId: string | number) => {
      onChange(values.filter(v => v !== String(tokenId)))
    },
    [values, onChange],
  )

  const handleSelectedChange = useCallback(
    (selected: SelectedItem | SelectedItem[]) => {
      if (!Array.isArray(selected)) return
      const newValues = selected
        .filter((s): s is SelectedItem => s != null)
        .map(s => s.text || s.id)
        .filter((t): t is string => !!t && !(variant === 'blocked' && t === '*'))
      onChange(newValues)
      setFilterVal('')
    },
    [onChange, variant],
  )

  // Client-side filter: hide already-selected items
  const customFilterFn = useCallback(
    (item: SelectedItem) => {
      if (!item) return false
      return !values.includes(item.text || item.id)
    },
    [values],
  )

  return (
    <div className={styles.wrapper}>
      <Autocomplete>
        <Autocomplete.Input
          aria-label={rest['aria-label']}
          as={TextInputWithTokens}
          tokens={tokens}
          onTokenRemove={onTokenRemove}
          value={filterVal}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const val = e.currentTarget.value
            setFilterVal(val)
            if (!val.trim()) {
              setItems([])
              setIsLoading(false)
            } else {
              setIsLoading(true)
            }
          }}
          placeholder={placeholder}
          autoComplete="off"
          size="small"
        />
        <Autocomplete.Overlay>
          <Autocomplete.Menu
            aria-labelledby={`autocomplete-tokens-${rest['aria-label'].replace(/\s+/g, '-')}`}
            items={items}
            loading={isLoading}
            selectedItemIds={selectedItemIds}
            filterFn={customFilterFn}
            emptyStateText={filterVal.trim() ? emptyText : `Type to search`}
            onSelectedChange={handleSelectedChange}
            selectionVariant="multiple"
          />
        </Autocomplete.Overlay>
      </Autocomplete>
    </div>
  )
}
